import React, { Component } from 'react';
import PropTypes from 'prop-types';

import Utils from '../utils';
import UserConfiguration from './UserConfiguration.component';
import EnterVerificationCode from './EnterVerificationCode.component';
import SuccessfulVerification from './SuccessfulVerification.component';

const { Session, Util, Communication, Logger, withLayoutAndModalContexts } = Utils;

class AccountVerify extends Component {
	constructor(props) {
		super(props);

		this.state = {
			step: '1',
			startAccountVerificationError: '',
			email: this.getEmail(),
			phoneNumber: this.getPhoneNumber(),
			displayEmailTextInEnterVerification: false,
		};
	}

	componentDidMount() {
		const { accountVerificationOption } = Session.getAccountInfo();

		const { isChangingEmailAddress, emailOnly } = this.props;

		// if user is changing their email address, immediately show the verification code step
		if (isChangingEmailAddress || emailOnly || accountVerificationOption === 'email') {
			this.setState(
				{
					step: '2',
				},
				() => {
					this.startAccountVerification('email');
				}
			);
		}
	}

	getEmail = () => {
		const { requestedEmail } = this.props;
		const { customerDetails = {} } = Session.getAccountInfo();

		// requestedEmail takes higher precedence over the verification email in the getAccountInfo if passed from URL query param
		return (
			requestedEmail ||
			(customerDetails.verificationEmail
				? customerDetails.verificationEmail
				: customerDetails.email)
		);
	};

	getPhoneNumber = () => {
		const { requestedPhoneNumber } = this.props;
		const {
			customerDetails = {},
			customerDetails: { address = {} },
		} = Session.getAccountInfo();

		// requestedPhoneNumber takes higher precedence over the verification phone number in the getAccountInfo if passed from URL query param
		return (
			requestedPhoneNumber ||
			(customerDetails.verificationPhoneNumber
				? `${customerDetails.verificationPhoneNumber} ext:${customerDetails.verificationPhoneExtension}`
				: address.phone)
		);
	};

	startAccountVerification = async communicationMethod => {
		const { phoneNumber, email } = this.state;
		const { isChangingEmailAddress } = this.props;
		const phoneNumberData = {
			PhoneNumber: Util.getPhoneNumber(phoneNumber),
			Extension: Util.getExtension(phoneNumber),
			AccountVerificationOption: communicationMethod,
		};
		const emailData = {
			Email: email,
			AccountVerificationOption: communicationMethod,
		};

		const startAccountVerificationResponse = await Communication.startAccountVerification(
			isChangingEmailAddress || communicationMethod === 'email' ? emailData : phoneNumberData
		);

		if (Communication.isSuccessful(startAccountVerificationResponse)) {
			this.setState({
				step: '2',
				startAccountVerificationError: '',
				displayEmailTextInEnterVerification: communicationMethod === 'email',
			});
		} else {
			this.setState({
				startAccountVerificationError: startAccountVerificationResponse.ErrorDescription,
			});
		}

		let codeType = '';
		switch (communicationMethod) {
			case 'email':
				codeType = 'email';
				break;
			case 'sms':
				codeType = 'text';
				break;
			default:
				codeType = 'phone';
		}

		Logger.logToServer({
			codeType,
			verificationStep: 'step1',
			error: startAccountVerificationResponse.ErrorDescription,
		});
	};

	nextStep = step => {
		const { showSuccess, completionCallback } = this.props;

		// call completionCallback if the user successfully verifies their phone number and we don't want to show the success message
		if (!showSuccess && step === '3') {
			completionCallback(true);
		} else {
			this.setState({ step });
		}
	};

	render() {
		const {
			phoneNumber,
			step,
			email,
			startAccountVerificationError,
			displayEmailTextInEnterVerification,
		} = this.state;
		const { layoutContext, isChangingEmailAddress, completionCallback, emailOnly } = this.props;
		const { isMobile } = layoutContext;

		return (
			<>
				{step === '1' && (
					<UserConfiguration
						isMobile={isMobile}
						email={email}
						phoneNumber={phoneNumber}
						startAccountVerificationError={startAccountVerificationError}
						startAccountVerification={this.startAccountVerification}
					/>
				)}
				{step === '2' && (
					<EnterVerificationCode
						email={email}
						phoneNumber={phoneNumber}
						isMobile={isMobile}
						isChangingEmailAddress={isChangingEmailAddress}
						startAccountVerificationError={startAccountVerificationError}
						displayEmailTextInEnterVerification={displayEmailTextInEnterVerification}
						startAccountVerification={this.startAccountVerification}
						nextStep={this.nextStep}
						emailOnly={emailOnly}
					/>
				)}
				{step === '3' && <SuccessfulVerification completionCallback={completionCallback} />}
			</>
		);
	}
}

export default withLayoutAndModalContexts(AccountVerify);

AccountVerify.defaultProps = {
	showSuccess: false,
	requestedEmail: '',
	requestedPhoneNumber: '',
	isChangingEmailAddress: false,
};

AccountVerify.propTypes = {
	// (Optional) `false` query param to control when to show success message
	showSuccess: PropTypes.bool,

	// (Optional) Email passed via query param
	requestedEmail: PropTypes.string,

	// (Optional) Phone number passed via query param
	requestedPhoneNumber: PropTypes.string,

	// (Optional) Used to determine if user is changing their email address then directly show the verification code step
	isChangingEmailAddress: PropTypes.bool,

	// A callback to follow through ONLY after user successfully verifies the phone number
	completionCallback: PropTypes.func.isRequired,
};
